import { SupportedDataType, UnknownSpec } from '../../core';
import { PluginRegistryProps } from '../components';
import { Plugin, PluginImplementation, PluginLoader, PluginModuleResource, PluginType } from '../model';

export type MockPlugin = {
  [T in PluginType]: {
    pluginType: T;
    kind: string;
    plugin: PluginImplementation<T>;
    metadata?: {
      supportedDataTypes?: SupportedDataType | SupportedDataType[];
    };
    display?: {
      isMock?: boolean;
    };
  };
}[PluginType];

export type MockDataQueryPluginType = MockPlugin & { pluginType: 'DataQuery' };

/**
 * Helper for mocking `PluginRegistry` data during tests. Returns props that can be spread on the `PluginRegistry`
 * component so that it will load the mock plugins you provide.
 */
export function mockPluginRegistry(...mockPlugins: MockPlugin[]): Omit<PluginRegistryProps, 'children'> {
  const mockPluginResource: PluginModuleResource = {
    kind: 'PluginModule',
    metadata: {
      name: 'Fake Plugin Module for Tests',
      created_at: '',
      updated_at: '',
      version: 0,
    },
    spec: {
      // Add metadata for all mock plugins
      plugins: mockPlugins.map(({ pluginType, kind, metadata, display }) => ({
        pluginType,
        kind,
        display: {
          name: getMockPluginName(pluginType, kind),
          ...display,
        },
        metadata,
      })),
    },
  };

  const mockPluginModule: Record<string, Plugin<UnknownSpec>> = {};
  for (const mockPlugin of mockPlugins) {
    // "Export" on the module under the same name as the kind the plugin handles
    mockPluginModule[mockPlugin.kind] = mockPlugin.plugin;
  }

  const pluginLoader: PluginLoader = {
    getInstalledPlugins() {
      return Promise.resolve([mockPluginResource]);
    },
    importPluginModule(/* resource */) {
      return Promise.resolve(mockPluginModule);
    },
  };

  return {
    pluginLoader,
    defaultPluginKinds: {
      DataQuery: 'PrometheusTimeSeriesQuery',
    },
  };
}

/**
 * The function that's used to generate the display name of mocked plugins in mockPluginRegistry. Can be useful if you
 * need to interact with some UI component that's displaying it.
 */
export function getMockPluginName(pluginType: PluginType, kind: string) {
  return `${pluginType} Plugin for ${kind}`;
}
